import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, ArrowLeft, RefreshCw, HelpCircle } from 'lucide-react';

const PaymentCancel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(15);

  // Récupérer les paramètres de l'URL
  const orderId = searchParams.get('order_id') || searchParams.get('orderId');
  const reason = searchParams.get('reason') || 'Paiement annulé par l\'utilisateur';

  // Redirection automatique après 15 secondes
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/subscription');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleRetry = () => {
    navigate('/subscription');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@teckia.com';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-500 via-pink-500 to-purple-600 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Card principale */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-pink-600 p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="animate-pulse">
                <XCircle size={80} className="text-white" strokeWidth={2.5} />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Paiement Annulé
            </h1>
            <p className="text-red-100 text-lg">
              Votre transaction a été annulée
            </p>
          </div>

          {/* Contenu */}
          <div className="p-8">
            {/* Message principal */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-6 text-center">
              <p className="text-gray-700 text-lg mb-4">
                Aucun montant n'a été débité de votre compte.
              </p>
              
              {orderId && (
                <div className="inline-block bg-white px-4 py-2 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500">Référence de commande</p>
                  <p className="font-mono text-sm font-semibold text-gray-800">
                    {orderId}
                  </p>
                </div>
              )}
            </div>

            {/* Raisons possibles */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <HelpCircle className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Pourquoi mon paiement a-t-il été annulé ?
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Vous avez cliqué sur "Annuler" sur la page de paiement</li>
                      <li>Votre session de paiement a expiré</li>
                      <li>Solde Orange Money insuffisant</li>
                      <li>Problème de connexion pendant le paiement</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Conseils */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Que faire maintenant ?
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Vérifiez votre solde Orange Money</li>
                      <li>Assurez-vous d'avoir une bonne connexion internet</li>
                      <li>Réessayez le paiement</li>
                      <li>Contactez le support si le problème persiste</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleRetry}
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg"
              >
                <RefreshCw size={20} />
                Réessayer le paiement
              </button>
              
              <button
                onClick={handleGoHome}
                className="flex-1 bg-white border-2 border-gray-300 text-gray-700 font-bold py-4 px-6 rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={20} />
                Retour à l'accueil
              </button>
            </div>

            {/* Bouton support */}
            <div className="mt-4">
              <button
                onClick={handleContactSupport}
                className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-all duration-300"
              >
                Contacter le support
              </button>
            </div>

            {/* Compte à rebours */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Redirection automatique vers les abonnements dans{' '}
                <span className="font-bold text-red-600">{countdown}</span> secondes...
              </p>
            </div>
          </div>
        </div>

        {/* Message de support */}
        <div className="mt-6 text-center">
          <p className="text-white text-sm">
            Besoin d'aide ? Contactez notre support :{' '}
            <a href="mailto:contact@tdidigital.io" className="underline hover:text-pink-200">
              contact@tdidigital.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancel;